import React from 'react';

import { Flex, Box } from '@chakra-ui/react';
import { Check as CheckIcon } from '@mesh.ai/rabbit';
import { useQueryClient } from '@tanstack/react-query';
import { useSelectReviewsSelectedIntent } from 'Deer/intent/Reviews/slice/ReviewsIntentSlice';
import { useToast } from 'components/hooks/useToast';
import { useHistory } from 'react-router-dom';
import { useSelectedView } from 'reducers/viewsSlice';
import { usePartialPublishEventDetails } from 'screens/performanceReview/helpers';
import { useCalibrationViewLandingQueryParams } from 'screens/performanceReview/manageAndInsights/TalentPlanning/helpers';
import { checkBulkNotifyEnabled, checkCalibrationStatusColumnEnabled } from 'utils/helpers/featureFlags';

import { Notifications, Publish } from 'assets/assetsV1/iconComponents';

import { sendBulkNotifyReviewsEvent } from 'constants/mixpanelHelpers/bulkNotifyEvents';
import {
  sendPPClearSelectionEvent,
  sendPPSelectAllEmployeesEvent,
  sendPPShareResultsButtonEvent,
} from 'constants/mixpanelHelpers/partialPublishEvents';
import { REVIEW_INTENTS } from 'constants/moduleIntentTypes';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';
import { routes } from 'constants/routes';

import { Button } from 'uiToolkitV1/Button';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useBulkNotifyContext } from '../BulkNotifyDrawer/BulkNotifyContext';
import { usePartialPublishContext } from '../ShareResultsDrawer/PartialPublishContext';
import { useTargetReviewCycleId } from '../ShareResultsDrawer/dataHooks';
import { useBulkActionContext } from './BulkActionContext';
import { BulkActionsSplitButton } from './BulkActionsSplitButton';
import { CalibrationStatus, updateCalibrationStatus } from './dataHooks';

export interface BulkActionHeaderProps {
  clearSelection: () => void;
  selectAll: () => void;
  isOnlyPublishAction?: boolean;
}

export type BulkActionOptionsType = {
  value: string;
  label: string;
  onClick?: () => void;
  icon?: typeof Publish | typeof CheckIcon | React.FC<unknown>;
  isDisabled?: boolean;
  isSubMenu?: boolean;
  subActions?: BulkActionOptionsType[];
};

export const BulkActionHeader: React.FC<BulkActionHeaderProps> = ({
  clearSelection,
  selectAll,
  isOnlyPublishAction = false,
}) => {
  const { toggleDrawer: toggleDrawerPublish } = usePartialPublishContext();
  const { toggleDrawer: toggleDrawerNotify } = useBulkNotifyContext() ?? {};
  const { selectedEmployeeIds, totalEmployees } = useBulkActionContext();
  const history = useHistory();
  const queryClient = useQueryClient();
  const selectedCohort = useSelectedView();
  const selectedReviewIntent = useSelectReviewsSelectedIntent();
  const reviewCycleId: string = useTargetReviewCycleId();
  const { calibrationViewId } = useCalibrationViewLandingQueryParams();
  const toast = useToast();

  const totalRows = totalEmployees;

  const totalRowsSelected = selectedEmployeeIds.length;

  const { module, empId, page } = usePartialPublishEventDetails();

  const selectAllWithEvent = () => {
    sendPPSelectAllEmployeesEvent({
      eventProperties: {
        page,
        source: module,
        empId,
      },
    });
    selectAll();
  };

  const clearSelectionWithEvent = () => {
    sendPPClearSelectionEvent({
      eventProperties: {
        page,
        source: module,
        empId,
      },
    });
    clearSelection();
  };

  const togglePublishDrawerWithEvent = () => {
    sendPPShareResultsButtonEvent({
      eventProperties: {
        page,
        source: module,
        empId,
      },
    });
    toggleDrawerPublish(true);
  };

  const toggleNotifyDrawerWithEvent = () => {
    sendBulkNotifyReviewsEvent({
      eventProperties: {
        page,
        source: module,
      },
    });
    toggleDrawerNotify(true);
  };

  let actions: BulkActionOptionsType[] = [
    {
      value: 'Share Results',
      label: 'Share Results',
      onClick: togglePublishDrawerWithEvent,
      icon: Publish,
      isDisabled: selectedEmployeeIds.length === 0,
    },
  ];

  if (checkBulkNotifyEnabled() && !isOnlyPublishAction) {
    actions = [
      {
        value: 'Notify',
        label: 'Notify Pending Reviewers',
        onClick: toggleNotifyDrawerWithEvent,
        icon: Notifications,
        isDisabled: selectedEmployeeIds.length === 0,
      },
      ...actions,
    ];
  }

  const handleCalibrationStatusUpdate = async (status: CalibrationStatus) => {
    // update calibration status using api
    try {
      await updateCalibrationStatus({
        employeeIds: selectedEmployeeIds,
        calibrationStatus: status,
        reviewCycleId: +reviewCycleId,
      });
      toast({ title: 'Calibration status updated successfully', status: 'success' });
    } catch (err) {
      console.error('Error in updating calibration status ', err);
      toast({ title: 'Error in updating calibration status. Please try again.', status: 'error' });
    }

    // refresh table and metric card
    clearSelection();
    const isTalentPlanningPage = history.location.pathname.includes(routes.performanceReview.talentPlanning);
    if (isTalentPlanningPage) {
      await queryClient.invalidateQueries([
        REACT_QUERY_KEYS.talentPlanning.tabularData,
        selectedCohort?.id,
        calibrationViewId,
      ]);
    } else {
      await queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewsManage]);
      await queryClient.invalidateQueries([
        REACT_QUERY_KEYS.reviewAiCalibrationMetrics,
        selectedCohort?.id,
        reviewCycleId,
      ]);
    }
  };

  const isCalibratedIntent = selectedReviewIntent?.id === REVIEW_INTENTS.REVIEW_CALIBRATED_RESULTS;
  const showCalibrationStatus =
    checkCalibrationStatusColumnEnabled() && isCalibratedIntent && selectedCohort?.id === -1;

  if (showCalibrationStatus) {
    actions = [
      ...actions,
      {
        value: 'Set calibration status',
        label: 'Set calibration status',
        onClick: () => {},
        icon: CheckIcon,
        isSubMenu: true,
        subActions: [
          {
            value: 'PENDING',
            label: 'Pending',
            onClick: () => handleCalibrationStatusUpdate('PENDING'),
          },
          {
            value: 'NOT_REQUIRED',
            label: 'Not Required',
            onClick: () => handleCalibrationStatusUpdate('NOT_REQUIRED'),
          },
          {
            value: 'COMPLETE',
            label: 'Complete',
            onClick: () => handleCalibrationStatusUpdate('COMPLETE'),
          },
        ],
      },
    ];
  }

  return (
    totalRows > 0 && (
      <Flex
        px="12px"
        py={1}
        alignItems="center"
        bgColor="neutral.F5F7F9"
        borderWidth="1px"
        borderColor="neutral.EBEFF3"
        borderRadius="4px"
        mb={3}
      >
        {totalRows === totalRowsSelected ? (
          <TextBodyMedium bold>
            All {totalRowsSelected > 1 ? `${totalRowsSelected} ` : ''}employees are selected
          </TextBodyMedium>
        ) : (
          totalRowsSelected > 0 && (
            <TextBodyMedium bold>
              Total {totalRowsSelected} {totalRowsSelected > 1 ? 'employees are' : 'employee is'} selected
            </TextBodyMedium>
          )
        )}
        {totalRows !== totalRowsSelected && (
          <Button variant="tertiary" onClick={selectAllWithEvent}>
            Select all {totalRows} Employees
          </Button>
        )}
        {totalRowsSelected > 0 && (
          <Button variant="tertiary" onClick={clearSelectionWithEvent}>
            Clear Selection
          </Button>
        )}
        <Box ml="auto">
          <BulkActionsSplitButton options={actions} isDisabled={selectedEmployeeIds.length === 0} />
        </Box>
      </Flex>
    )
  );
};
